/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * InfoTip — dependency-free hover/click popover for in-context help.
 *
 * Why custom? Three reasons:
 *  1. Zero new dependencies. The dashboard is committed to a tight bundle.
 *  2. Full control over the slate-900/red-600 dark theme.
 *  3. Uses position:fixed + getBoundingClientRect so it escapes overflow:hidden
 *     containers like the precinct comparison tables. No portal needed.
 *
 * Usage:
 *
 *   <InfoTip termId="voter_score" />
 *   <InfoTip termId="drop_off">Drop-off rate</InfoTip>
 *   <InfoTip term={{ title: 'Custom', short: '...', long: '...', ... }} />
 *
 * Behavior:
 *  - Hover over the trigger (or focus it) → opens after a 100ms delay.
 *  - Click the trigger → toggles open and "pins" it (no auto-close on mouseout).
 *  - Press Escape, click outside, or scroll → closes.
 *
 * The popover renders the linked GlossaryTerm: title + short blurb + optional
 * formula/swatches/examples + a "Learn more →" link that drills to the
 * /glossary view scrolled to the right entry. Clicking the link uses a
 * lightweight CustomEvent ('infotip:navigate') so the InfoTip doesn't have
 * to know about the parent's drill handler.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { GLOSSARY, type GlossaryTerm } from '@/lib/glossary';

type InfoTipProps = {
  /** Glossary id to look up. Either this or `term` must be provided. */
  termId?: string;
  /** Inline term, useful for one-off explanations not worth a glossary entry. */
  term?: Partial<GlossaryTerm> & { title: string; short: string };
  /** Optional children render the trigger label. If absent, just a (?) icon. */
  children?: React.ReactNode;
  /** Render the (?) icon even when children are present. Default: true. */
  showIcon?: boolean;
  /** Tailwind class overrides for the trigger wrapper. */
  className?: string;
  /** Compact mode shrinks the icon — useful inside table headers. */
  compact?: boolean;
};

const TIP_WIDTH = 320; // px
const TIP_OFFSET = 8; // px gap from trigger
const HOVER_DELAY = 120; // ms before hover-open fires

function dispatchNavigate(termId: string) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent('infotip:navigate', { detail: { termId } }),
  );
}

export const InfoTip: React.FC<InfoTipProps> = ({
  termId,
  term: termOverride,
  children,
  showIcon = true,
  className = '',
  compact = false,
}) => {
  const term: GlossaryTerm | null = (() => {
    if (termOverride) return { id: termId ?? 'inline', category: 'data', long: '', ...termOverride } as GlossaryTerm;
    if (termId && GLOSSARY[termId]) return GLOSSARY[termId];
    return null;
  })();

  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const hoverTimer = useRef<number | null>(null);

  const recomputePosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Try below; if it would clip the viewport, flip above.
    const wantTop = r.bottom + TIP_OFFSET;
    const popHeight = popoverRef.current?.getBoundingClientRect().height ?? 200;
    const top = wantTop + popHeight > vh ? r.top - TIP_OFFSET - popHeight : wantTop;
    // Anchor left to the trigger but clamp inside the viewport.
    let left = r.left;
    if (left + TIP_WIDTH + 16 > vw) left = vw - TIP_WIDTH - 16;
    if (left < 8) left = 8;
    setCoords({ top: Math.max(8, top), left });
  };

  // Reposition on open + on scroll/resize while open.
  useLayoutEffect(() => {
    if (!open) return;
    recomputePosition();
    const handler = () => {
      if (pinned) {
        recomputePosition();
      } else {
        setOpen(false);
      }
    };
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [open, pinned]);

  // ESC + click-outside to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setPinned(false);
      }
    };
    const onClick = (e: MouseEvent) => {
      if (!popoverRef.current || !triggerRef.current) return;
      const t = e.target as Node;
      if (popoverRef.current.contains(t) || triggerRef.current.contains(t)) return;
      setOpen(false);
      setPinned(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  const handleMouseEnter = () => {
    if (pinned) return;
    if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => setOpen(true), HOVER_DELAY);
  };
  const handleMouseLeave = () => {
    if (pinned) return;
    if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => setOpen(false), HOVER_DELAY);
  };
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (open && pinned) {
      setOpen(false);
      setPinned(false);
    } else {
      setOpen(true);
      setPinned(true);
      // recompute on next frame so popoverRef is mounted
      requestAnimationFrame(recomputePosition);
    }
  };

  if (!term) {
    // Glossary id not found — render children without decoration so we
    // don't break the layout in dev. The console warn helps catch typos.
    if (termId && typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn(`[InfoTip] Unknown glossary id: ${termId}`);
    }
    return <>{children}</>;
  }

  // 44×44 tap area (WCAG 2.5.5). Visual bullet stays small; padding
  // carries the hit region so it matches the design while passing audits.
  const visualIconClass = compact
    ? 'inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-bg-elevated text-fg-secondary text-[9px] font-bold leading-none transition group-hover:bg-accent group-hover:text-white'
    : 'inline-flex items-center justify-center w-4 h-4 rounded-full bg-bg-elevated text-fg-secondary text-[10px] font-bold leading-none transition group-hover:bg-accent group-hover:text-white';

  return (
    <>
      <span
        ref={triggerRef}
        className={`inline-flex items-center ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
        {showIcon && (
          <button
            type="button"
            aria-label={`What is ${term.title}?`}
            aria-expanded={open}
            className="group inline-flex items-center justify-center min-h-11 min-w-11 p-2 -m-2 ml-0 rounded cursor-help bg-transparent"
            onClick={handleClick}
            onFocus={() => setOpen(true)}
            onBlur={() => !pinned && setOpen(false)}
          >
            <span className={visualIconClass} aria-hidden="true">?</span>
          </button>
        )}
      </span>
      {open && coords && (
        <div
          ref={popoverRef}
          role="tooltip"
          className="fixed z-[9999] rounded-lg border border-border-subtle bg-bg-base shadow-2xl shadow-black/60 p-4 text-sm text-fg-secondary"
          style={{ top: coords.top, left: coords.left, width: TIP_WIDTH }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="font-bold text-fg-primary text-[15px] leading-tight">
              {term.title}
            </div>
            {pinned && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setPinned(false);
                }}
                className="text-fg-muted hover:text-fg-primary text-lg leading-none -mt-1"
                aria-label="Close"
              >
                ×
              </button>
            )}
          </div>
          <div className="text-fg-secondary leading-snug">{term.short}</div>

          {term.formula && (
            <div className="mt-3 rounded bg-bg-raised border border-border-base px-2 py-1.5 font-mono text-[11px] text-status-success overflow-x-auto">
              {term.formula}
            </div>
          )}

          {term.swatches && term.swatches.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {term.swatches.map((s, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1.5 rounded bg-bg-raised border border-border-base px-2 py-0.5 text-[11px] text-fg-secondary"
                >
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-sm"
                    style={{ backgroundColor: s.color }}
                  />
                  {s.label}
                </span>
              ))}
            </div>
          )}

          {term.examples && term.examples.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold mb-1">
                Examples
              </div>
              <ul className="space-y-1 text-[12px] text-fg-secondary">
                {term.examples.map((ex, i) => (
                  <li key={i} className="flex gap-1.5">
                    <span className="text-fg-muted">•</span>
                    <span>{ex}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-3 pt-2 border-t border-border-base/70 flex items-center justify-between">
            <button
              type="button"
              className="text-[11px] text-accent-fg hover:text-accent-fg/80 font-medium"
              onClick={(e) => {
                e.stopPropagation();
                dispatchNavigate(term.id);
                setOpen(false);
                setPinned(false);
              }}
            >
              Learn more →
            </button>
            <span className="text-[10px] uppercase tracking-wide text-fg-muted">
              {term.category}
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default InfoTip;
